import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router';
import { QueryClient, QueryCache, MutationCache, onlineManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Network } from '@capacitor/network';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';
import { PlatformProvider } from './contexts/PlatformContext';
import { ToastProvider } from '@/context/ToastContext';
import * as Sentry from "@sentry/react";
import { ScrollToTop } from './components/ScrollToTop';
import { RouteTracker } from './components/RouteTracker';
import { useAppStore } from './store/appStore';
import { usePlatform } from './hooks/usePlatform';
import { useToast } from '@/hooks/useToast';
import { coreApiClient } from './services/api/core';
import { GreekBrainPaywallModal } from './components/GreekBrainPaywallModal';
import type { AppType } from './types';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { checkForLiveUpdate } from './services/liveUpdate';
import { hapticService } from './services/haptics';

function ExternalRedirect({ to }: { to: string }) {
    useEffect(() => { window.location.replace(to); }, [to]);
    return null;
}
import {
    getCacheKey,
    getTelegramUserId,
    getStoredUserId,
    clearUserData,
    storeUserId,
} from './utils/userStorage';
import { detectWebAppType } from './utils/webAppType';
import { BUILT_PRODUCT, PRODUCTS, isProductId, otherProduct } from './product';
import { useThemeController } from './theme/theme';
import { LoadingScreen } from './components/LoadingScreen';
import ErrorFallback from './components/ErrorFallback';


// Session marker for fresh session detection
const SESSION_MARKER_KEY = 'app_session_active';
const CACHE_CLEARED_KEY = 'cache_cleared_for_session';

// On native (single-user device) we WANT the persisted React Query cache to
// survive cold starts so the app can boot offline from disk. The fresh-session
// wipe below exists for Telegram Mini App multi-user privacy (a shared Telegram
// session can switch accounts) and must NOT run on native cold starts.
const isNativePlatform = Capacitor.isNativePlatform();

// Detect fresh session and handle user change synchronously at module load
const isFreshSession = !sessionStorage.getItem(SESSION_MARKER_KEY);
const storedUserId = getStoredUserId();
const currentUserId = getTelegramUserId();
const userChanged = !!(currentUserId && storedUserId && currentUserId !== storedUserId);

// Store new user ID if needed
if (currentUserId && currentUserId !== storedUserId) {
    storeUserId(currentUserId);
}

// Clear cache on fresh session or user change
if (isFreshSession || userChanged) {
    sessionStorage.setItem(SESSION_MARKER_KEY, 'true');

    if (userChanged) {
        // A real account switch — always purge the previous user's data.
        sessionStorage.setItem(CACHE_CLEARED_KEY, 'pending');
        clearUserData(storedUserId || undefined);
    } else if (!isNativePlatform) {
        // Fresh web/Telegram session — clear stale IndexedDB cache for privacy.
        // Skipped on native so offline cold-start can rehydrate from disk.
        sessionStorage.setItem(CACHE_CLEARED_KEY, 'pending');
        Promise.all([
            del('REACT_QUERY_OFFLINE_CACHE'),
            del(getCacheKey()),
        ]).finally(() => sessionStorage.setItem(CACHE_CLEARED_KEY, 'done'));
    }
    // else: native fresh session — keep the persisted cache and leave
    // CACHE_CLEARED_KEY unset so the persister rehydrates it normally.
}

// Create persister with user-namespaced key
const persister = createAsyncStoragePersister({
    storage: {
        getItem: async () => {
            if (sessionStorage.getItem(CACHE_CLEARED_KEY) === 'pending') return null;
            return await get(getCacheKey());
        },
        setItem: async (_key, value) => {
            try {
                const parsed = JSON.parse(value);
                if (parsed?.clientState?.queries) {
                    parsed.clientState.queries = parsed.clientState.queries.filter((q: { queryKey?: unknown[]; state?: { data?: unknown } }) =>
                        !(q.queryKey?.[0] === 'hallouminati-dashboard' && q.state?.data === null)
                    );
                    await set(getCacheKey(), JSON.stringify(parsed));
                    return;
                }
            } catch { /* ignore */ }
            await set(getCacheKey(), value);
        },
        removeItem: async () => del(getCacheKey()),
    },
});

import { ApiError } from './services/api/core';

/**
 * Smart retry logic for React Query:
 * - Retry network errors and 5xx errors (transient issues)
 * - Don't retry 4xx errors (client errors won't change on retry)
 */
const shouldRetry = (failureCount: number, error: Error): boolean => {
    if (failureCount >= 2) return false; // Max 2 retries

    if (error instanceof ApiError) {
        return error.isRetryable;
    }

    // For unknown errors, retry once
    return failureCount < 1;
};

// Drive React Query's online state from the Capacitor Network plugin, which is
// authoritative on native (navigator.onLine is unreliable inside WKWebView).
// This is what makes queries that failed/paused offline auto-refetch
// (refetchOnReconnect) the moment connectivity actually returns.
onlineManager.setEventListener((setOnline) => {
    const listenerHandle = Network.addListener('networkStatusChange', (status) => {
        setOnline(status.connected);
    });
    Network.getStatus()
        .then((status) => setOnline(status.connected))
        .catch(() => { /* plugin unavailable — keep React Query's default */ });
    return () => {
        listenerHandle.then((handle) => handle.remove()).catch(() => { });
    };
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: shouldRetry,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
            // Retain inactive queries long enough that the persisted cache stays
            // useful for "show old data offline". The persister maxAge below caps
            // how stale the on-disk snapshot may be across cold starts.
            gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days
        },
        mutations: {
            retry: shouldRetry,
        },
    },
    queryCache: new QueryCache({
        onError: (error) => {
            console.error('Global Query Error:', error);
            // Only capture non-ApiError exceptions (ApiErrors are handled in core.ts)
            // Also skip if already reported to prevent duplicates
            if (!(error instanceof ApiError) || !error.sentryReported) {
                if (error instanceof ApiError && !error.isNetworkError && !error.isUnauthorized && !error.isEntitlementError) {
                    error.sentryReported = true;
                    Sentry.captureException(error);
                } else if (!(error instanceof ApiError)) {
                    Sentry.captureException(error);
                }
            }
        },
    }),
    mutationCache: new MutationCache({
        onError: (error) => {
            console.error('Global Mutation Error:', error);
            // Only capture non-ApiError exceptions (ApiErrors are handled in core.ts)
            // Skip GreekBrain entitlement errors — handled by the paywall / limit toast.
            if (!(error instanceof ApiError) || !error.sentryReported) {
                if (error instanceof ApiError && !error.isNetworkError && !error.isUnauthorized && !error.isEntitlementError) {
                    error.sentryReported = true;
                    Sentry.captureException(error);
                } else if (!(error instanceof ApiError)) {
                    Sentry.captureException(error);
                }
            }
        },
    }),
});

/**
 * Which product's router to mount, loaded on demand.
 *
 * The guards read `import.meta.env.VITE_APP` directly (not BUILT_PRODUCT) so
 * Vite's define step turns them into literals and the dead branch — the other
 * product's `import()` — is dropped from a pinned build. A universal build
 * (no VITE_APP) keeps both and picks at runtime.
 */
const HallouminatiRouter = import.meta.env.VITE_APP === 'greekbrain'
    ? null
    : lazy(() => import('./products/hallouminati'));
const GreekBrainRouter = import.meta.env.VITE_APP === 'hallouminati'
    ? null
    : lazy(() => import('./products/greekbrain'));

/**
 * AppRouter resolves the product from the URL (`/:appType/*`) or, on native,
 * from the platform, then mounts that product's router.
 */
function AppRouter() {
    const params = useParams<{ appType: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { appType: detectedAppType, isNativeApp } = usePlatform();

    useEffect(() => {
        // For native platforms, redirect to correct path if needed
        if (isNativeApp && !location.pathname.startsWith(`/${detectedAppType}`)) {
            navigate(`/${detectedAppType}`, { replace: true });
        }
    }, [isNativeApp, detectedAppType, navigate, location.pathname]);

    // Determine final appType: use detected appType for native, URL param for web/telegram
    const urlAppType = isProductId(params.appType) ? params.appType : null;
    const appType: AppType = isNativeApp ? detectedAppType : (urlAppType ?? 'hallouminati');
    // Light / dark for this product — Hallouminati only (docs/design/dark-mode-plan.md).
    useThemeController(appType);

    // A pinned build serving the other product's URL (a legacy
    // app.hallouminati.app/greekbrain link, a stale Telegram button) sends the
    // browser to that product's own host, path and query intact.
    const crossProductUrl = !isNativeApp && BUILT_PRODUCT && urlAppType && urlAppType !== BUILT_PRODUCT
        ? `${otherProduct(BUILT_PRODUCT).appUrl}${location.pathname}${location.search}`
        : null;
    useEffect(() => {
        if (crossProductUrl) window.location.replace(crossProductUrl);
    }, [crossProductUrl]);
    if (crossProductUrl) return null;

    // Sync appType to store immediately so children read the correct value on first render
    const storeAppType = useAppStore.getState().appType;
    if (storeAppType !== appType) {
        useAppStore.getState().setAppType(appType);
    }

    if (!isNativeApp && !urlAppType) {
        // Invalid appType - redirect to default
        //TODO throw 404 error
        return <Navigate to="/hallouminati" replace />;
    }

    const ProductRouter = appType === 'hallouminati' ? HallouminatiRouter : GreekBrainRouter;
    if (!ProductRouter) {
        // Only reachable in a pinned build on native with a mismatched bundle id
        // — a packaging error, not a user state. Say so rather than white-screen.
        Sentry.captureMessage(`Bundle built for ${BUILT_PRODUCT} launched as ${appType}`);
        return <ErrorFallback />;
    }

    return (
        <Suspense fallback={<LoadingScreen appType={appType} />}>
            <ProductRouter />
        </Suspense>
    );
}

/**
 * Mounts the GreekBrain subscription paywall and wires the global API
 * entitlement handler. Lives above the router so it works on every route
 * (including standalone pages like the voice-dialogue session that aren't
 * inside the tab shell). 402 → open paywall; 429 QUOTA_EXCEEDED → limit toast.
 */
function GreekBrainEntitlementLayer() {
    const { showToast } = useToast();
    const openGreekbrainPaywall = useAppStore((s) => s.openGreekbrainPaywall);

    useEffect(() => {
        coreApiClient.setEntitlementHandler((kind, message) => {
            if (kind === 'subscription') {
                openGreekbrainPaywall('api_gate', message);
            } else {
                showToast(message, 'info');
            }
        });
    }, [showToast, openGreekbrainPaywall]);

    return <GreekBrainPaywallModal />;
}

function App({ isTelegramContext }: { isTelegramContext: boolean }) {
    // App-wide tap haptics: every button/link press gets the standard tick.
    useEffect(() => {
        return hapticService.installGlobalTapHaptics();
    }, []);

    // Signal a successful boot to Capgo's OTA watchdog. If this never fires (a bad
    // OTA bundle white-screens before mount), Capgo auto-reverts to the last good
    // bundle on next launch. Native-only; no-op on web/Telegram.
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        CapacitorUpdater.notifyAppReady().catch((error) => {
            console.warn('CapacitorUpdater.notifyAppReady failed:', error);
            Sentry.captureException(error);
        });
        // Check for a fresher web bundle and stage it for the next cold start.
        // No-op on web/Telegram and on dev builds (see liveUpdate.ts).
        void checkForLiveUpdate();
    }, []);

    // Handle late Telegram SDK loading - check if user ID changed after SDK loads
    useEffect(() => {
        if (!isTelegramContext) return;

        const checkTelegramUser = () => {
            const currentId = getTelegramUserId();
            const storedId = getStoredUserId();

            if (currentId && storedId && currentId !== storedId) {
                console.log(`[App] Late Telegram user detection: ${storedId} -> ${currentId}`);
                clearUserData(storedId);
                storeUserId(currentId);
                window.location.reload();
            } else if (currentId && !storedId) {
                storeUserId(currentId);
            }
        };

        checkTelegramUser();
        const timeoutId = setTimeout(checkTelegramUser, 500);
        return () => clearTimeout(timeoutId);
    }, [isTelegramContext]);

    return (
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{ persister, maxAge: 1000 * 60 * 60 * 24 * 30 }}
        >
            <PlatformProvider isTelegramContext={isTelegramContext}>
                <ToastProvider>
                    <GreekBrainEntitlementLayer />
                    <BrowserRouter>
                        <ScrollToTop />
                        <RouteTracker />
                        <Routes>
                            <Route path="/legal/terms" element={<ExternalRedirect to={PRODUCTS.hallouminati.legalUrl('terms')} />} />
                            <Route path="/legal/privacy" element={<ExternalRedirect to={PRODUCTS.hallouminati.legalUrl('privacy')} />} />


                            {/* Dynamic route with parameter */}
                            <Route path="/:appType/*" element={<AppRouter />} />

                            {/* Default redirect based on platform */}
                            <Route
                                path="/"
                                element={<PlatformRedirect />}
                            />

                            {/* Catch all - redirect to root */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </BrowserRouter>
                </ToastProvider>
            </PlatformProvider>
        </PersistQueryClientProvider>
    );
}

/**
 * PlatformRedirect component that uses platform context
 */
function PlatformRedirect() {
    const { isNativeApp, appType } = usePlatform();

    if (isNativeApp) {
        return <Navigate to={`/${appType}`} replace />;
    }

    // For web/Telegram: hostname first (app.greekbrain.app serves GreekBrain
    // from `/`), then pathname for the shared app.hallouminati.app host.
    return <Navigate to={`/${detectWebAppType()}`} replace />;
}

export default App;